import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePomodoroStore } from '../store';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import { getTranslation } from '../utils/pomodoroTranslations';
import { Play, Pause, Square, Timer, Zap, Coffee } from 'lucide-react';

const MODE_CONFIG = {
  focus: { icon: Zap, color: 'from-primary to-primary/80' },
  short_break: { icon: Coffee, color: 'from-green-500 to-green-600' },
  long_break: { icon: Coffee, color: 'from-blue-500 to-blue-600' },
};

export function FloatingPomodoroTimer() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isRunning, mode, endTs, remainingMs, subject, customMinutes, sessionsCompleted,
    sessionJustCompleted, pauseTimer, resumeTimer, stopTimer, completeSession, clearJustCompleted,
    language
  } = usePomodoroStore();

  // Translation helper
  const t = (key) => getTranslation(language, key);

  const [timeLeft, setTimeLeft] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Don't show on pomodoro page
  const isOnPomodoroPage = location.pathname === '/pomodoro';
  const shouldShow = (isRunning || remainingMs !== null) && !isOnPomodoroPage;

  // Tick: calculate remaining time from endTs
  useEffect(() => {
    if (!isRunning || !endTs) return;
    const tick = () => {
      const remaining = Math.max(0, endTs - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) {
        handleComplete();
      }
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [isRunning, endTs]);

  // Show paused remaining
  useEffect(() => {
    if (!isRunning && remainingMs !== null) {
      setTimeLeft(remainingMs);
    }
  }, [isRunning, remainingMs]);

  // Handle session complete notification
  useEffect(() => {
    if (sessionJustCompleted) {
      const toastKey = mode === 'focus' ? 'toast_short_break_complete' : 
                       mode === 'short_break' ? 'toast_focus_complete' : 'toast_long_break_complete';
      const msg = t(toastKey);
      toast.success(msg, { duration: 6000, position: 'top-center' });
      clearJustCompleted();
      // Save to backend
      saveSession();
    }
  }, [sessionJustCompleted]);

  const handleComplete = async () => {
    const prevMode = usePomodoroStore.getState().mode;
    completeSession();
    setTimeLeft(0);
  };

  const saveSession = async () => {
    try {
      const token = localStorage.getItem('mentis_session_token');
      const prevMode = mode;
      if (prevMode === 'focus') {
        const modeToSave = prevMode;
        const mins = customMinutes || 25;
        await fetch(`${API_URL}/pomodoro/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ type: modeToSave, duration_minutes: mins, subject, completed: true })
        });
      }
    } catch (e) { /* ignore */ }
  };

  const mins = Math.floor(timeLeft / 60000);
  const secs = Math.floor((timeLeft % 60000) / 1000);
  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.focus;
  const ModeIcon = modeConfig.icon;

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: 120, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 120, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
          className="fixed bottom-6 right-6 z-50"
          data-testid="floating-pomodoro"
        >
          <div
            className={`bg-gradient-to-br ${modeConfig.color} text-white rounded-2xl shadow-xl shadow-black/20 overflow-hidden`}
            style={{ minWidth: '140px' }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-2 px-3 pt-2.5 pb-1 cursor-pointer"
              onClick={() => navigate('/pomodoro')}
            >
              <ModeIcon className="w-3.5 h-3.5 opacity-90" />
              <span className="text-xs font-medium opacity-90">{t(mode)}</span>
              {subject !== 'Allgemein' && (
                <span className="text-xs opacity-70 truncate max-w-[60px]">{subject}</span>
              )}
            </div>

            {/* Timer */}
            <div
              className="text-center px-3 pb-1 cursor-pointer"
              onClick={() => navigate('/pomodoro')}
            >
              <span className="text-3xl font-mono font-bold tracking-tight">
                {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between px-2 pb-2.5 gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); isRunning ? pauseTimer() : resumeTimer(); }}
                className="flex-1 flex items-center justify-center gap-1 bg-white/20 hover:bg-white/30 rounded-lg py-1.5 text-xs font-medium transition-all"
                data-testid="float-pause-resume"
              >
                {isRunning ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {isRunning ? t('pause') : t('resume')}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); stopTimer(); }}
                className="bg-white/20 hover:bg-white/30 rounded-lg p-1.5 transition-all"
                data-testid="float-stop"
              >
                <Square className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIsVisible(false); setTimeout(() => setIsVisible(true), 100); }}
                className="text-white/60 hover:text-white text-xs px-1"
                title="Minimieren"
              >
                ×
              </button>
            </div>

            {/* Session dots */}
            <div className="flex justify-center gap-1 pb-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i < (sessionsCompleted % 4) ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
